// Package imports:
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { faCaretDown, faCaretUp, faEdit, faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import Toast from "react-bootstrap/esm/Toast";
import ToastContainer from "react-bootstrap/esm/ToastContainer";
// Component imports:
import Alert, { AlertType } from "../../ui-elements/Alert/Alert";
import Button from "../../ui-elements/Button/Button";
import DisplayBox from "../../ui-elements/DisplayBox/DisplayBox";
import DropSelect from "../../ui-elements/DropSelect/DropSelect";
import Input from "../../ui-elements/Input/Input";
import Label from "../../ui-elements/Label/Label";
import Link from "../../ui-elements/Link/Link";
import Table from "../../ui-elements/Table/Table";
import PepAddForeignWatchModal from "./PepWatchModals/PepAddForeignWatchModal";
import PepEditWatchModal from "./PepWatchModals/PepEditWatchModal";
import PepWatchListModal from "./PepWatchModals/PepWatchAddPersonsListModal";
import PepWatchSanctionsConnectionModal from "./PepWatchModals/PepWatchSanctionsConnectionModal";
import PepWatchPEPConnectionModal from "./PepWatchModals/PepWatchPEPConnectionModal";
import Tooltip from "../../ui-elements/Tooltip/Tooltip";
// Service imports:
import { GET_KELDAN_API_URL, TOAST_DELAY } from "../../services/config";
import { capitalizeFirstLetter, getTableDate, insertDash, isSSN } from "../../services/utils";
// Type imports:
import { PepWatchListData } from "../../types/PepTypes";
import { IKeldanApiResponse } from "../../types/KeldanTypes";
import Bugsnag from "@bugsnag/js";
import { IPepWatchResponse } from "./PepWatch";
import { PaginationDropdown, PaginationFlipper, useChunkedPagination } from "../../services/paginationHooks";
import GeniusLabel from "../../ui-elements/GeniusLabel/GeniusLabel";

interface PepWatchTableSectionProps {
  isDisabled?: boolean;
}

// Define filter types for type safety
type PepStatusFilter = "showAll" | "yes" | "no" | "invalid" | "active" | "inactive";
type LastChangeFilter = "showAll" | "onPep" | "offPep" | "onSanctions" | "offSanctions" | "none" | "all";
type SanctionsStatusFilter = "showAll" | "yes" | "no" | "active" | "inactive";

// Server-side sorting types
type SortBy = "name" | "change" | null;
type SortOrder = "asc" | "desc" | null;

// Define the consolidated filter state type including server-side sorting
interface FilterState {
  searchTerm: string;
  pepStatus: PepStatusFilter;
  lastChange: LastChangeFilter;
  sanctionsStatus: SanctionsStatusFilter;
  sortBy: SortBy;
  sortOrder: SortOrder;
}

const NOSSN = "999999-9999";
const CHUNK_SIZE = 500; // Fetch 500 items at a time

const PepWatchTableSection: React.FC<PepWatchTableSectionProps> = ({ isDisabled = true }) => {
  // Toast states
  const [showToast, setToastShow] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastType, setToastType] = useState<AlertType>("success");

  // Modal states
  const [addToWatchModal_isOpen, showAddToWatchModal] = useState(false);
  const [editWatchModal_isOpen, showEditWatchModal] = useState(false);
  const [editWatchModal_data, setEditWatchModal_data] = useState<PepWatchListData | undefined>(undefined);

  const [addForeignWatchModal_isOpen, showAddForeignWatchModal] = useState(false);
  const [addForeignWatchModal_data, setAddForeignWatchModal_data] = useState<PepWatchListData | undefined>(undefined);

  const [SANCTIONS_connectionModal_isOpen, setSANCTIONS_ConnectionModal_isOpen] = useState(false);
  const [SANCTIONS_connectionModal_data, setSANCTIONS_ConnectionModal_data] = useState<string | undefined>(undefined);

  const [PEP_connectionModal_isOpen, setPEP_ConnectionModal_isOpen] = useState(false);
  const [PEP_connectionModal_data, setPEP_ConnectionModal_data] = useState<string | undefined>(undefined);

  // Data and loading states
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [loadedData, setLoadedData] = useState<PepWatchListData[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Consolidated filter state including server-side sorting
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    pepStatus: "showAll",
    lastChange: "showAll",
    sanctionsStatus: "showAll",
    sortBy: null, // No default sorting
    sortOrder: null, // No default sort order
  });

  // Track the search input separately for immediate UI updates
  const [searchInputValue, setSearchInputValue] = useState<string>("");

  // Debounce timer ref for search
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track current filter signature to detect changes and prevent initial load conflicts
  const currentFiltersRef = useRef<string>("");
  const hasInitializedRef = useRef<boolean>(false);

  // Function to handle column header clicks for server-side sorting
  const handleServerSort = useCallback((columnSortBy: SortBy) => {
    if (!columnSortBy) return;

    setFilters((prev) => {
      // If clicking the same column, toggle between asc -> desc -> none
      if (prev.sortBy === columnSortBy) {
        if (prev.sortOrder === "asc") {
          return { ...prev, sortOrder: "desc" };
        } else if (prev.sortOrder === "desc") {
          return { ...prev, sortBy: null, sortOrder: null };
        } else {
          return { ...prev, sortOrder: "asc" };
        }
      } else {
        // If clicking a different column, start with ascending order
        return { ...prev, sortBy: columnSortBy, sortOrder: "asc" };
      }
    });
  }, []);

  // Function to get sort indicator for column headers
  const getSortIndicator = useCallback(
    (columnSortBy: SortBy): JSX.Element | null => {
      if (filters.sortBy === columnSortBy) {
        if (filters.sortOrder === "asc")
          return (
            <span className={cx("caret-span", { "is-ascending": true })}>
              <FontAwesomeIcon icon={faCaretUp} className="sort-indicator" />
            </span>
          );
        if (filters.sortOrder === "desc")
          return (
            <span className={cx("caret-span", { "is-descending": true })}>
              <FontAwesomeIcon icon={faCaretDown} className="sort-indicator" />
            </span>
          );
      }
      return null;
    },
    [filters.sortBy, filters.sortOrder]
  );

  // Clickable column header component for server-side sorting
  const SortableColumnHeader: React.FC<{
    title: string;
    sortBy: SortBy;
    onClick: (sortBy: SortBy) => void;
    getSortIndicator: (sortBy: SortBy) => JSX.Element | null;
    disabled?: boolean;
  }> = ({ title, sortBy, onClick, getSortIndicator, disabled = false }) => {
    const isLoading = isInitialLoading || pagination.isLoadingMore;

    return (
      <div
        onClick={disabled || isLoading ? undefined : () => onClick(sortBy)}
        style={{
          // pointerEvents: disabled || isLoading ? "none" : undefined,
          cursor: disabled || isLoading ? 'text' : "pointer",
          width: "100%",
          position: "relative",
        }}
      >
        <span style={{ position: "relative" }}>
          {title} {getSortIndicator(sortBy)}
        </span>
      </div>
    );
  };
  // Helper function to build the API URL with filters, pagination, and sorting
  function buildPepWatchListFilterString(
  ): string {
    let url = ''
    // Add search term filter if present
    if (filters.searchTerm.trim() !== "") {
      url += `&searchTerm=${encodeURIComponent(filters.searchTerm)}`;
    }

    // Add PEP status filter if not "showAll"
    if (filters.pepStatus !== "showAll") {
      url += `&filterPep=${filters.pepStatus}`;
    }

    // Add last change filter if not "showAll" (note: there's a typo in the API - "filterChange")
    if (filters.lastChange !== "showAll") {
      url += `&filterChange=${filters.lastChange}`;
    }

    // Add sanctions status filter if not "showAll"
    if (filters.sanctionsStatus !== "showAll") {
      url += `&filterSanctions=${filters.sanctionsStatus}`;
    }

    // Add server-side sorting parameters
    if (filters.sortBy) {
      url += `&sortBy=${filters.sortBy}`;
    }
    if (filters.sortOrder) {
      url += `&order=${filters.sortOrder}`;
    }

    return url;
  }
  // API fetch function for chunked data loading with server-side sorting
  const fetchChunkedData = useCallback(
    async (startIndex: number, chunkSize: number): Promise<PepWatchListData[]> => {
      try {
        // Build the API URL with filters, pagination, and sorting parameters
        let url = `${GET_KELDAN_API_URL()}/Pep/GetPepWatchList?start=${startIndex}&limit=${chunkSize}&includeCount=true`;
        const filterString = buildPepWatchListFilterString();
        if (filterString && filterString.length > 0) {
          url += filterString;
        }

        const headers = {
          "Content-Type": "application/json; charset=utf-8",
        };

        const response = await fetch(url, {
          method: "GET",
          headers,
        });
        const responseBody: IKeldanApiResponse<IPepWatchResponse<PepWatchListData[]>> = await response.json();

        if (response.ok && responseBody.result) {

          // Update total count only on first fetch or when filters change
          if (startIndex === 0) {
            setTotalCount(responseBody.result.count || 0);
          }

          return responseBody.result.data || [];
        } else {
          throw new Error(responseBody.message || "Failed to fetch data");
        }
      } catch (error) {
        console.error("Error fetching chunk data:", error);
        if (error instanceof Error) {
          Bugsnag.notify(error);
          setToastMessage(error.message);
          setToastType("error");
          setToastShow(true);
        }
        return [];
      }
    },
    [filters]
  );

  // Add a ref to track what we've already fetched
  const lastFetchedIndexRef = useRef<number>(0);

  const handleFetchMoreData = useCallback(
    async (startIndex: number, chunkSize: number): Promise<PepWatchListData[]> => {
      // Prevent fetching the same range twice
      if (startIndex < lastFetchedIndexRef.current) {
        return [];
      }

      // Prevent fetching beyond the total count
      if (startIndex >= totalCount && totalCount > 0) {
        return [];
      }

      const newData = await fetchChunkedData(startIndex, chunkSize);

      // Update our tracking reference
      if (newData.length > 0) {
        lastFetchedIndexRef.current = startIndex + newData.length;

        // Simply append the new data - trust the API
        setLoadedData((prevData) => [...prevData, ...newData]);
      }

      return newData;
    },
    [fetchChunkedData, totalCount]
  );

  // Reset the tracking ref when filters change
  const handleFiltersChange = useCallback(() => {
    setLoadedData([]);
    setTotalCount(0);
    lastFetchedIndexRef.current = 0; // Reset the tracker
  }, []);

  // Memoize the pagination configuration to prevent unnecessary re-initialization
  const paginationConfig = useMemo(
    () => ({
      chunkSize: CHUNK_SIZE, // Always use 500
      onFetchMoreData: handleFetchMoreData,
      onFiltersChange: handleFiltersChange,
      prefetchThreshold: 3, // Prefetch 3 pages ahead
    }),
    [handleFetchMoreData, handleFiltersChange]
  );

  // Initialize chunked pagination with memoized configuration
  const pagination = useChunkedPagination<PepWatchListData>(
    totalCount,
    loadedData,
    paginationConfig,
    10, // Default items per page for display
    0 // Start at page 0
  );

  // Function to perform initial data load or reset data after filter changes
  const loadInitialData = useCallback(async () => {
    try {
      setIsInitialLoading(true);

      // Clear existing data
      setLoadedData([]);

      // Fetch first chunk
      const initialData = await fetchChunkedData(0, CHUNK_SIZE);
      setLoadedData(initialData);
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setIsInitialLoading(false);
    }
  }, [fetchChunkedData]);

  // Handle search input changes with debouncing
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;

    // Update the input value immediately for responsive UI
    setSearchInputValue(value);

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce the filter update
    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        searchTerm: value,
      }));
    }, 500);
  };

  // Handle PEP status filter changes
  const handlePepStatusFilterChange = (option: string) => {
    setFilters((prev) => ({
      ...prev,
      pepStatus: option as PepStatusFilter,
    }));
  };

  // Handle last change filter changes
  const handleLastChangeFilterChange = (option: string) => {
    setFilters((prev) => ({
      ...prev,
      lastChange: option as LastChangeFilter,
    }));
  };

  // Handle sanctions status filter changes
  const handleSanctionsStatusFilterChange = (option: string) => {
    setFilters((prev) => ({
      ...prev,
      sanctionsStatus: option as SanctionsStatusFilter,
    }));
  };
  // Initial data load on component mount
  useEffect(() => {
    // Skip initialization if component is disabled
    if (isDisabled) {
      setIsInitialLoading(false);
      return;
    }

    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      loadInitialData();
    }
  }, [isDisabled]); // Added isDisabled to dependency array

  // Effect to handle filter changes and reload data (now includes sorting changes)
  useEffect(() => {
    // Skip filter-based reloading if component is disabled
    if (isDisabled) {
      return;
    }

    const filtersSignature = JSON.stringify(filters);

    // Only reload if filters actually changed AND we've already initialized
    if (filtersSignature !== currentFiltersRef.current && hasInitializedRef.current) {
      currentFiltersRef.current = filtersSignature;
      loadInitialData();
    } else if (!hasInitializedRef.current) {
      // Set initial filter signature on first run
      currentFiltersRef.current = filtersSignature;
    }
  }, [filters, loadInitialData, isDisabled]); // Added isDisabled to dependency array

  // Effect to handle isDisabled prop changes - clear data when disabled, load when enabled
  useEffect(() => {
    if (isDisabled) {
      // Clear data when component becomes disabled
      setLoadedData([]);
      setTotalCount(0);
      setIsInitialLoading(false);
      hasInitializedRef.current = false; // Reset initialization flag
    } else if (hasInitializedRef.current === false) {
      // Load data when component becomes enabled (only if not already initialized)
      hasInitializedRef.current = true;
      loadInitialData();
    }
  }, [isDisabled, loadInitialData]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Success callback for modals to refresh data
  const handleModalSuccess = useCallback(() => {
    loadInitialData();
  }, [loadInitialData]);
  return (
    <>
      <div className="pep_watch_results section--gray">
        <div className="shell">
          <DisplayBox>
            <div className="filter-wrapper">
              {/* Search input with debounced onChange */}
              <Input
                disabled={isDisabled}
                onChange={handleSearchChange}
                value={searchInputValue}
                className="input-field"
                label="Leita"
                withSearchGlassIcon
              />
              <div className="dropdown-field">
                {/* PEP status filter dropdown */}
                <DropSelect
                  disabled={isDisabled}
                  className={"dropdown-field"}
                  label="Stjórnmálaleg tengsl"
                  options={[
                    { label: "Sýna allt", value: "showAll" },
                    { label: "Já", value: "yes" },
                    { label: "Nei", value: "no" },
                    { label: "Ógild kennitala", value: "invalid" },
                    { label: "Í vöktun", value: "active" },
                    { label: "Ekki í vöktun", value: "inactive" },
                  ]}
                  defaultValue={"showAll"}
                  onChange={handlePepStatusFilterChange}
                  size="lg"
                />
              </div>
              <div className="dropdown-field">
                {/* Sanctions status filter dropdown */}
                <DropSelect
                  disabled={isDisabled}
                  className={"dropdown-field"}
                  label="Alþjóðlegir válistar"
                  options={[
                    { label: "Sýna allt", value: "showAll" },
                    { label: "Já", value: "yes" },
                    { label: "Nei", value: "no" },
                    { label: "Í vöktun", value: "active" },
                    { label: "Ekki í vöktun", value: "inactive" },
                  ]}
                  defaultValue={"showAll"}
                  onChange={handleSanctionsStatusFilterChange}
                  size="lg"
                />
              </div>
              <div className="dropdown-field">
                {/* Last change filter dropdown */}
                <DropSelect
                  disabled={isDisabled}
                  className={"dropdown-field"}
                  label="Breytingar"
                  options={[
                    { label: "Sýna allt", value: "showAll" },
                    { label: "Á PEP lista", value: "onPep" },
                    { label: "Af PEP lista", value: "offPep" },
                    { label: "Á alþjóðlegum válista", value: "onSanctions" },
                    { label: "Af alþjóðlegum válista", value: "offSanctions" },
                    { label: "Allar breytingar", value: "all" },
                    { label: "Engar breytingar", value: "none" },
                  ]}
                  defaultValue={"showAll"}
                  onChange={handleLastChangeFilterChange}
                  size="lg"
                />
              </div>
            </div>
            <>
              {/* Main data table with server-side sorting for name and change columns */}
              <Table
                showLoading={isInitialLoading || (pagination.isLoadingMore && (pagination.loadedItemsCount - pagination.itemsPerPage) < pagination.currentPageIndex * pagination.itemsPerPage)}
                tableSize="lg"
                data={pagination.currentData} // Use the current page data from pagination hook
                showEmptyTableText
                aSide={
                  <Button
                    disabled={isDisabled}
                    size="lg"
                    icon={faPlusCircle}
                    onClick={() => {
                      showAddToWatchModal(true);
                    }}
                    buttonType="secondary"
                  >
                    Bæta við viðskiptavinum
                  </Button>
                }
                bSide={
                  <>
                    <Link icon="excel" disabled={isDisabled} url={`/Excel/PepWatchList?${buildPepWatchListFilterString()}`}>
                      Gögn í excel
                    </Link>
                    <PaginationDropdown disabled={isDisabled} paginationConfig={{ usePortal: true }} paginationState={pagination} />
                  </>
                }
                rowClassName={(item) => {
                  if (item.pepFrequency === "never" && item.sanctionsFrequency === "never") {
                    return "row-inactive";
                  }
                }}
                columns={[
                  {
                    // Server-side sortable name column
                    title: (
                      <SortableColumnHeader
                        title="Nafn"
                        sortBy="name"
                        onClick={handleServerSort}
                        getSortIndicator={getSortIndicator}
                        disabled={isDisabled}
                      />
                    ),
                    renderCell: ({ personFirstName, personMiddleName, personLastName }) =>
                      `${personFirstName} ${personMiddleName ? personMiddleName + " " : ""}${personLastName}`,
                    textAlign: "left",
                    minColumnWidth: "230px", // Minimum width for name column
                    serverSideSortable: true, // Enable server-side sorting for this column
                  },
                  {
                    title: "Kennitala",
                    renderCell: ({ id: { personId } }) => (isSSN(personId) ? insertDash(personId) : NOSSN),
                    textAlign: "left",
                  },
                  {
                    title: "Stjórnmálaleg tengsl (PEP)",
                    renderCell: ({ pepStatus, id: { personId }, lastPepCheckTimestamp }) => {
                      if (!isSSN(personId)) return "Ógild kennitala";
                      if (!lastPepCheckTimestamp) {
                        return "Í bið";
                      }
                      return (
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <Tooltip
                            text={
                              <Label labelType={pepStatus ? "yes" : "no"} size="lg" text={pepStatus ? "Já" : "Nei"} />
                            }
                            tooltip={
                              <p>
                                Síðasta uppfletting:{" "}
                                {lastPepCheckTimestamp
                                  ? getTableDate(new Date(lastPepCheckTimestamp.time), "DD/MM/YYYY", ".", "")
                                  : "Óþekkt"}
                              </p>
                            }
                          />
                          {pepStatus && (
                            <Button
                              buttonType="secondary"
                              size="sm"
                              onClick={() => {
                                setPEP_ConnectionModal_isOpen(true);
                                setPEP_ConnectionModal_data(personId);
                              }}
                            >
                              Skoða
                            </Button>
                          )}
                        </div>
                      );
                    },
                    textAlign: "left",
                  },
                  {
                    title: "Alþjóðlegir válistar",
                    renderCell: (item) =>
                      item.sanctionsFrequency === "never" ? (
                        <Link
                          onClick={() => {
                            showAddForeignWatchModal(true);
                            setAddForeignWatchModal_data(item);
                          }}
                          icon="more"
                        >
                          Vakta
                        </Link>
                      ) : item.lastSanctionsCheckTimestamp === null ? (
                        <div>í bið</div>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <Tooltip
                            text={
                              <Label
                                labelType={item.sanctionsStatus ? "yes" : "no"}
                                size="lg"
                                text={item.sanctionsStatus ? "Já" : "Nei"}
                              />
                            }
                            tooltip={
                              <p>
                                Síðasta uppfletting:{" "}
                                {item.lastSanctionsCheckTimestamp
                                  ? getTableDate(
                                    new Date(item.lastSanctionsCheckTimestamp.time),
                                    "DD/MM/YYYY",
                                    ".",
                                    ""
                                  )
                                  : "Óþekkt"}
                              </p>
                            }
                          />
                          {item.sanctionsStatus && (
                            <Button
                              buttonType="secondary"
                              size="sm"
                              onClick={() => {
                                setSANCTIONS_ConnectionModal_isOpen(true);
                                setSANCTIONS_ConnectionModal_data(item.id.personId);
                              }}
                            >
                              Skoða
                            </Button>
                          )}
                        </div>
                      ),
                    textAlign: "left",
                  },
                  {
                    title: "Nýjasta breyting",
                    renderCell: ({ lastChangeComment }) => capitalizeFirstLetter(lastChangeComment),
                    textAlign: "left",
                  },
                  {
                    title: (
                      <SortableColumnHeader
                        title="Síðasta uppfærsla"
                        sortBy="change"
                        onClick={handleServerSort}
                        getSortIndicator={getSortIndicator}
                        disabled={isDisabled} />
                    ),
                    renderCell: ({ lastChangeTimestamp, timeStampCreated }) =>
                      lastChangeTimestamp
                        ? getTableDate(new Date(lastChangeTimestamp.time), "DD/MM/YYYY", ".", "")
                        : timeStampCreated
                          ? getTableDate(new Date(timeStampCreated.time), "DD/MM/YYYY", ".", "")
                          : "",
                    textAlign: "left",
                    serverSideSortable: true, // Enable server-side sorting for this column
                  },
                  {
                    title: "Tilvísun",
                    renderCell: ({ reference }) => {
                      if (reference.length < 11) {
                        return reference;
                      } else {
                        return reference.slice(0, 10) + "...";
                      }
                    },
                    textAlign: "left",
                  },
                  {
                    title: "Viðskiptavinur",
                    renderCell: (item) => {
                      const inActive = item.pepFrequency === "never" && item.sanctionsFrequency === "never";
                      return (
                        <div className="pepWatchTable-customer">
                          <FontAwesomeIcon
                            icon={faEdit}
                            className="fa-edit-icon"
                            onClick={() => {
                              setEditWatchModal_data(item);
                              showEditWatchModal(true);
                            }}
                          />
                          <GeniusLabel active={!inActive
                          } />
                        </div>
                      );
                    },
                    textAlign: "left",
                  },
                  {
                    title: "Skýrsla",
                    renderCell: (item) => (
                      <Button
                        buttonType="primary"
                        size="sm"
                        anchorProps={{ href: `/Valistar/Voktun-Skyrsla/${item.id.personId}` }}
                      >
                        Sækja
                      </Button>
                    ),
                  },
                ]}
              />

              {/* Pagination controls */}
              <PaginationFlipper paginationState={pagination} />
            </>
          </DisplayBox>
        </div>
      </div>
      {/* Modal components with success callbacks that refresh data */}
      <PepWatchListModal
        setToastMessage={setToastMessage}
        setToastType={setToastType}
        setToastShow={setToastShow}
        show={addToWatchModal_isOpen}
        close={() => showAddToWatchModal(false)}
        onSuccess={handleModalSuccess}
      />
      <PepEditWatchModal
        setToastMessage={setToastMessage}
        setToastType={setToastType}
        setToastShow={setToastShow}
        data={editWatchModal_data}
        show={editWatchModal_isOpen}
        close={() => showEditWatchModal(false)}
        onSuccess={handleModalSuccess}
      />
      <PepAddForeignWatchModal
        personData={addForeignWatchModal_data}
        setToastMessage={setToastMessage}
        setToastType={setToastType}
        setToastShow={setToastShow}
        show={addForeignWatchModal_isOpen}
        close={() => showAddForeignWatchModal(false)}
        onSuccess={handleModalSuccess}
      />
      <PepWatchSanctionsConnectionModal
        show={SANCTIONS_connectionModal_isOpen}
        close={() => setSANCTIONS_ConnectionModal_isOpen(false)}
        id={SANCTIONS_connectionModal_data}
      />
      <PepWatchPEPConnectionModal
        show={PEP_connectionModal_isOpen}
        close={() => setPEP_ConnectionModal_isOpen(false)}
        id={PEP_connectionModal_data}
      />
      {/* Toast notifications */}
      <ToastContainer className="toastContainer">
        <Toast onClose={() => setToastShow(false)} autohide delay={TOAST_DELAY} show={showToast}>
          <Alert type={toastType} headText={toastMessage} />
        </Toast>
      </ToastContainer>
    </>
  );
};

export default PepWatchTableSection;